import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { FiSave, FiArrowLeft, FiLoader, FiUser, FiMail, FiPhone, FiShield, FiUsers, FiTrash2, FiLock } from 'react-icons/fi';
import { useNavigate, useParams } from 'react-router-dom';
import { usuariosAPI, veiculosAPI } from '../services/api';
import theme from '../styles/theme';
import Layout from '../components/Layout';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[6]};
  max-width: 800px;
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[4]};
  margin-bottom: ${theme.spacing[4]};
`;

const BackButton = styled.button`
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  background: transparent;
  border: 1px solid ${theme.colors.border.primary};
  color: ${theme.colors.text.secondary};
  border-radius: ${theme.borderRadius.lg};
  font-size: ${theme.fontSizes.base};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};

  &:hover {
    background: ${theme.colors.background.primary};
    color: ${theme.colors.primary[500]};
    border-color: ${theme.colors.primary[500]};
  }
`;

const Title = styled.h1`
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes['2xl']};
  font-weight: 700;
  margin: 0;
`;

const Form = styled.form`
  background: ${theme.colors.background.secondary};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.xl};
  padding: ${theme.spacing[8]};
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[6]};
`;

const FormSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[4]};
`;

const SectionTitle = styled.h3`
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes.lg};
  font-weight: 600;
  margin: 0;
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: ${theme.spacing[4]};
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[2]};
`;

const Label = styled.label`
  color: ${theme.colors.text.secondary};
  font-size: ${theme.fontSizes.sm};
  font-weight: 600;
`;

const Required = styled.span`
  color: ${theme.colors.error[500]};
`;

const Input = styled.input`
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.lg};
  background: ${theme.colors.background.primary};
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes.base};
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary[500]};
    box-shadow: 0 0 0 3px ${theme.colors.primary[500]}20;
  }

  &::placeholder {
    color: ${theme.colors.text.muted};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const Select = styled.select`
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.lg};
  background: ${theme.colors.background.primary};
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes.base};
  cursor: pointer;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary[500]};
    box-shadow: 0 0 0 3px ${theme.colors.primary[500]}20;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  option {
    background: ${theme.colors.background.primary};
    color: ${theme.colors.text.primary};
  }
`;

const CheckboxGroup = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
`;

const Checkbox = styled.input`
  width: 18px;
  height: 18px;
  cursor: pointer;
`;

const CheckboxLabel = styled.label`
  color: ${theme.colors.text.secondary};
  font-size: ${theme.fontSizes.base};
  cursor: pointer;
`;

const ClientesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: ${theme.spacing[2]};
`;

const ClienteButton = styled.button`
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  border: 2px solid ${props => props.$selected ? theme.colors.primary[500] : theme.colors.border.primary};
  border-radius: ${theme.borderRadius.lg};
  background: ${props => props.$selected ? theme.colors.primary[500] : theme.colors.background.primary};
  color: ${props => props.$selected ? theme.colors.text.primary : theme.colors.text.secondary};
  font-size: ${theme.fontSizes.sm};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  text-align: center;

  &:hover {
    border-color: ${theme.colors.primary[400]};
    background: ${props => props.$selected ? theme.colors.primary[600] : theme.colors.background.secondary};
  }
`;

const Actions = styled.div`
  display: flex;
  gap: ${theme.spacing[4]};
  justify-content: flex-end;
  padding-top: ${theme.spacing[6]};
  border-top: 1px solid ${theme.colors.border.primary};
`;

const Button = styled.button`
  padding: ${theme.spacing[3]} ${theme.spacing[6]};
  border: 1px solid ${props => {
    if (props.$danger) return theme.colors.error[500];
    if (props.$primary) return theme.colors.primary[500];
    return theme.colors.border.primary;
  }};
  background: ${props => {
    if (props.$danger) return theme.colors.error[500];
    if (props.$primary) return theme.colors.primary[500];
    return 'transparent';
  }};
  color: ${props => {
    if (props.$danger || props.$primary) return theme.colors.text.primary;
    return theme.colors.text.secondary;
  }};
  border-radius: ${theme.borderRadius.lg};
  font-size: ${theme.fontSizes.base};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  min-width: 120px;
  justify-content: center;

  &:hover:not(:disabled) {
    background: ${props => {
      if (props.$danger) return theme.colors.error[600];
      if (props.$primary) return theme.colors.primary[600];
      return theme.colors.background.primary;
    }};
    color: ${props => {
      if (props.$danger || props.$primary) return theme.colors.text.primary;
      return theme.colors.primary[500];
    }};
    border-color: ${props => {
      if (props.$danger) return theme.colors.error[600];
      if (props.$primary) return theme.colors.primary[600];
      return theme.colors.primary[500];
    }};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ButtonIcon = styled.div`
  font-size: ${theme.fontSizes.base};
`;

const ErrorMessage = styled.div`
  background: ${theme.colors.error[500]}20;
  border: 1px solid ${theme.colors.error[500]};
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing[4]};
  color: ${theme.colors.error[600]};
  font-size: ${theme.fontSizes.sm};
`;

const SuccessMessage = styled.div`
  background: ${theme.colors.success[500]}20;
  border: 1px solid ${theme.colors.success[500]};
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing[4]};
  color: ${theme.colors.success[600]};
  font-size: ${theme.fontSizes.sm};
`;

// Modal de confirmação
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: ${theme.colors.background.secondary};
  border-radius: ${theme.borderRadius.xl};
  padding: ${theme.spacing[8]};
  max-width: 500px;
  width: 90%;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
`;

const ModalTitle = styled.h3`
  color: ${theme.colors.error[500]};
  font-size: ${theme.fontSizes.xl};
  font-weight: 700;
  margin: 0 0 ${theme.spacing[4]} 0;
`;

const ModalMessage = styled.p`
  color: ${theme.colors.text.secondary};
  font-size: ${theme.fontSizes.base};
  line-height: 1.5;
  margin: 0 0 ${theme.spacing[6]} 0;
`;

const ModalActions = styled.div`
  display: flex;
  gap: ${theme.spacing[3]};
  justify-content: flex-end;
`;

const ModalButton = styled.button`
  padding: ${theme.spacing[3]} ${theme.spacing[6]};
  border: 1px solid ${props => props.$danger ? theme.colors.error[500] : theme.colors.border.primary};
  background: ${props => props.$danger ? theme.colors.error[500] : 'transparent'};
  color: ${props => props.$danger ? theme.colors.text.primary : theme.colors.text.secondary};
  border-radius: ${theme.borderRadius.lg};
  font-size: ${theme.fontSizes.base};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};

  &:hover {
    background: ${props => props.$danger ? theme.colors.error[600] : theme.colors.background.secondary};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const UsuarioForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditing = id && id !== 'novo';

  const [formData, setFormData] = useState({
    nome: '',
    email: '',
    telefone: '',
    ativo: true,
    clientes_ids: [],
    permissao_id: null
  });

  const [clientes, setClientes] = useState([]);
  const [permissoes, setPermissoes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(isEditing);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [resettingPassword, setResettingPassword] = useState(false);
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  const [loadingPermissoes, setLoadingPermissoes] = useState(true);

  // Carregar dados do usuário se estiver editando
  useEffect(() => {
    if (isEditing) {
      carregarUsuario();
    }
  }, [id]);

  // Carregar clientes
  useEffect(() => {
    carregarClientes();
  }, []);

  // Carregar permissões
  useEffect(() => {
    carregarPermissoes();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Máscara de telefone brasileira (formato: (11) 99999-9999)
  const formatTelefone = (value) => {
    const digits = String(value || '').replace(/\D/g, '').slice(0, 11);
    if (!digits) return '';
    if (digits.length <= 2) return `(${digits}`;
    if (digits.length <= 6) return `(${digits.slice(0,2)}) ${digits.slice(2)}`;
    if (digits.length <= 10) return `(${digits.slice(0,2)}) ${digits.slice(2,6)}-${digits.slice(6)}`;
    return `(${digits.slice(0,2)}) ${digits.slice(2,7)}-${digits.slice(7)}`;
  };

  const carregarUsuario = async () => {
    try {
      setLoadingData(true);
      const response = await usuariosAPI.buscarPorId(id);
      
      if (response.data.success) {
        const usuario = response.data.data;
        setFormData({
          nome: usuario.nome || '',
          email: usuario.email || '',
          telefone: usuario.telefone ? formatTelefone(usuario.telefone) : '',
          ativo: usuario.ativo,
          clientes_ids: usuario.clientes ? usuario.clientes.map(c => c.id) : [],
          permissao_id: usuario.permissao?.id || usuario.permissao_id || null
        });
      } else {
        setError('Erro ao carregar dados do usuário');
      }
    } catch (err) {
      console.error('Erro ao carregar usuário:', err);
      setError('Erro ao conectar com o servidor');
    } finally {
      setLoadingData(false);
    }
  };

  const carregarClientes = async () => {
    try {
      const response = await veiculosAPI.listarClientes();
      if (response.data.success) {
        setClientes(response.data.data);
      }
    } catch (err) {
      console.error('Erro ao carregar clientes:', err);
    }
  };

  const carregarPermissoes = async () => {
    try {
      setLoadingPermissoes(true);
      const response = await usuariosAPI.listarPermissoes();
      if (response.data.success) {
        const lista = response.data.data || [];
        setPermissoes(lista);

        if (!isEditing) {
          const permissaoPadrao =
            lista.find((item) => item.codigo === 'BASICO') || lista[0] || null;

          if (permissaoPadrao) {
            setFormData((prev) => {
              if (prev.permissao_id) {
                return prev;
              }
              return {
                ...prev,
                permissao_id: permissaoPadrao.id,
              };
            });
          }
        }
      }
    } catch (err) {
      console.error('Erro ao carregar permissões:', err);
    } finally {
      setLoadingPermissoes(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handlePermissaoChange = (value) => {
    const permissaoSelecionada = value ? Number(value) : null;
    if (import.meta.env.DEV) {
      console.debug('[UsuarioForm] handlePermissaoChange chamado:', {
        value,
        value_type: typeof value,
        permissaoSelecionada,
        permissaoSelecionada_type: typeof permissaoSelecionada
      });
    }
    setFormData(prev => {
      const newData = {
        ...prev,
        permissao_id: permissaoSelecionada
      };
      if (import.meta.env.DEV) {
        console.debug('[UsuarioForm] formData atualizado:', {
          ...newData,
          permissao_id: newData.permissao_id,
          permissao_id_type: typeof newData.permissao_id
        });
      }
      return newData;
    });
  };

  const handleClienteToggle = (clienteId) => {
    setFormData(prev => {
      const clientesAtuais = prev.clientes_ids || [];
      const clienteExiste = clientesAtuais.includes(clienteId);
      
      let novosClientes;
      if (clienteExiste) {
        // Remover cliente
        novosClientes = clientesAtuais.filter(id => id !== clienteId);
      } else {
        // Adicionar cliente
        novosClientes = [...clientesAtuais, clienteId];
      }
      
      return {
        ...prev,
        clientes_ids: novosClientes
      };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      setError(null);
      setSuccess(null);

      // Validações básicas
      if (!formData.nome.trim()) {
        throw new Error('Nome é obrigatório');
      }
      if (!formData.email.trim()) {
        throw new Error('Email é obrigatório');
      }
      if (!formData.permissao_id) {
        throw new Error('Permissão é obrigatória');
      }
      
      // Validação: pelo menos 1 cliente deve ser selecionado
      if (!isEditing && (!formData.clientes_ids || formData.clientes_ids.length === 0)) {
        throw new Error('É necessário selecionar pelo menos um cliente para o usuário');
      }

      const dadosParaEnvio = {
        nome: formData.nome && formData.nome.trim() ? formData.nome.trim() : '',
        email: formData.email && formData.email.trim() ? formData.email.trim() : '',
        telefone: formData.telefone && formData.telefone.trim() ? formData.telefone.trim() : null,
        ativo: Boolean(formData.ativo),
        clientes_ids: Array.isArray(formData.clientes_ids) ? formData.clientes_ids : [],
        permissao_id: formData.permissao_id ? Number(formData.permissao_id) : undefined
      };

      if (import.meta.env.DEV) {
        console.debug('[UsuarioForm] === ENVIANDO DADOS ===');
        console.debug('[UsuarioForm] formData.permissao_id:', formData.permissao_id, '(tipo:', typeof formData.permissao_id, ')');
        console.debug('[UsuarioForm] dadosParaEnvio.permissao_id:', dadosParaEnvio.permissao_id, '(tipo:', typeof dadosParaEnvio.permissao_id, ')');
        console.debug('[UsuarioForm] dadosParaEnvio completo:', dadosParaEnvio);
      }

      let response;
      if (isEditing) {
        response = await usuariosAPI.atualizar(id, dadosParaEnvio);
      } else {
        response = await usuariosAPI.criar(dadosParaEnvio);
      }

      if (response.data.success) {
        setSuccess(isEditing ? 'Usuário atualizado com sucesso!' : 'Usuário pré-cadastrado com sucesso! Um email de confirmação foi enviado para o usuário.');
        setTimeout(() => {
          navigate('/usuarios');
        }, 1500);
      } else {
        setError(response.data.error || 'Erro ao salvar usuário');
      }
    } catch (err) {
      console.error('Erro ao salvar usuário:', err);
      
      // Tratar erros específicos do backend
      if (err.response?.data?.error) {
        const errorMessage = err.response.data.error;
        
        // Mensagens amigáveis para erros específicos
        if (errorMessage.includes('Email já está em uso por um usuário ativo')) {
          setError('Este email já está sendo usado por outro usuário. Por favor, use um email diferente.');
        } else if (errorMessage.includes('Já existe um pré-cadastro pendente para este email')) {
          setError('Já existe um pré-cadastro pendente para este email. Aguarde a confirmação ou use um email diferente.');
        } else if (errorMessage.includes('Nome e email são obrigatórios')) {
          setError('Por favor, preencha todos os campos obrigatórios (Nome e Email).');
        } else {
          setError(errorMessage);
        }
      } else if (err.message) {
        // Tratar erros de validação do frontend
        if (err.message.includes('É necessário selecionar pelo menos um cliente')) {
          setError('É necessário selecionar pelo menos um cliente para o usuário.');
        } else {
          setError(err.message);
        }
      } else {
        setError('Erro ao conectar com o servidor. Tente novamente.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleVoltar = () => {
    navigate('/usuarios');
  };

  // Função para excluir usuário
  const handleExcluirUsuario = async () => {
    try {
      setDeleting(true);
      setError(null);

      await usuariosAPI.deletar(id);
      
      // Redirecionar para lista de usuários
      navigate('/usuarios');
    } catch (err) {
      console.error('Erro ao excluir usuário:', err);
      setError('Erro ao excluir usuário');
    } finally {
      setDeleting(false);
      setShowDeleteModal(false);
    }
  };

  // Função para abrir modal de confirmação
  const handleAbrirModalExclusao = () => {
    setShowDeleteModal(true);
  };

  // Função para cancelar exclusão
  const handleCancelarExclusao = () => {
    setShowDeleteModal(false);
  };

  // Função para limpar senha do usuário
  const handleLimparSenha = async () => {
    try {
      setResettingPassword(true);
      setError(null);

      const response = await usuariosAPI.limparSenha(id);
      
      if (response.data.success) {
        setSuccess('Senha limpa com sucesso! O usuário precisará definir uma nova senha no próximo login.');
        setShowResetPasswordModal(false);
        setTimeout(() => {
          setSuccess(null);
        }, 3000);
      } else {
        setError(response.data.error || 'Erro ao limpar senha');
      }
    } catch (err) {
      console.error('Erro ao limpar senha:', err);
      if (err.response?.data?.error) {
        setError(err.response.data.error);
      } else {
        setError('Erro ao conectar com o servidor');
      }
    } finally {
      setResettingPassword(false);
    }
  };

  // Função para abrir modal de confirmação de limpeza de senha
  const handleAbrirModalResetSenha = () => {
    setShowResetPasswordModal(true);
  };

  // Função para cancelar limpeza de senha
  const handleCancelarResetSenha = () => {
    setShowResetPasswordModal(false);
  };

  if (loadingData) {
    return (
      <Layout title="Carregando...">
        <Container>
          <div style={{ display: 'flex', justifyContent: 'center', padding: '4rem' }}>
            <FiLoader style={{ fontSize: '2rem', animation: 'spin 1s linear infinite' }} />
          </div>
        </Container>
      </Layout>
    );
  }

  return (
    <Layout title={isEditing ? 'Editar Usuário' : 'Novo Usuário - Pré-cadastro'}>
      <Container>
        <Header>
          <BackButton onClick={handleVoltar}>
            <FiArrowLeft />
            Voltar
          </BackButton>
          <Title>
            {isEditing ? 'Editar Usuário' : 'Novo Usuário - Pré-cadastro'}
          </Title>
        </Header>

        <Form onSubmit={handleSubmit}>
          {error && <ErrorMessage>{error}</ErrorMessage>}
          {success && <SuccessMessage>{success}</SuccessMessage>}

          <FormSection>
            <SectionTitle>
              <FiUser />
              Informações Pessoais
            </SectionTitle>
            
            <FormGrid>
              <FormGroup>
                <Label htmlFor="nome">
                  Nome <Required>*</Required>
                </Label>
                <Input
                  id="nome"
                  type="text"
                  placeholder="Nome completo do usuário"
                  value={formData.nome}
                  onChange={(e) => handleInputChange('nome', e.target.value)}
                  required
                />
              </FormGroup>

              <FormGroup>
                <Label htmlFor="email">
                  Email <Required>*</Required>
                </Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="email@exemplo.com"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  required
                />
              </FormGroup>

              <FormGroup>
                <Label htmlFor="telefone">Telefone</Label>
                <Input
                  id="telefone"
                  type="tel"
                  placeholder="(11) 99999-9999"
                  value={formData.telefone}
                  onChange={(e) => handleInputChange('telefone', formatTelefone(e.target.value))}
                />
              </FormGroup>

            </FormGrid>
          </FormSection>

          <FormSection>
            <SectionTitle>
              <FiShield />
              Configurações
            </SectionTitle>
            
          <FormGroup>
            <Label htmlFor="permissao">
              Permissão <Required>*</Required>
            </Label>
            <Select
              id="permissao"
              value={
                formData.permissao_id !== null && formData.permissao_id !== undefined
                  ? String(formData.permissao_id)
                  : ''
              }
              onChange={(e) => handlePermissaoChange(e.target.value)}
              disabled={loadingPermissoes}
            >
              <option value="" disabled>
                {loadingPermissoes ? 'Carregando permissões...' : 'Selecione uma permissão'}
              </option>
              {permissoes.map((permissao) => (
                <option key={permissao.id} value={permissao.id}>
                  {permissao.descricao || permissao.codigo}
                </option>
              ))}
            </Select>
          </FormGroup>

            <FormGroup>
              <CheckboxGroup>
                <Checkbox
                  id="ativo"
                  type="checkbox"
                  checked={formData.ativo}
                  onChange={(e) => handleInputChange('ativo', e.target.checked)}
                />
                <CheckboxLabel htmlFor="ativo">
                  Usuário ativo
                </CheckboxLabel>
              </CheckboxGroup>
            </FormGroup>
          </FormSection>

          <FormSection>
            <SectionTitle>
              <FiUsers />
              Clientes Associados
            </SectionTitle>
            
            <FormGroup>
              <Label>Selecione os clientes que este usuário pode acessar:</Label>
              <ClientesGrid>
                {clientes.map((cliente) => (
                  <ClienteButton
                    key={cliente.id}
                    type="button"
                    $selected={formData.clientes_ids?.includes(cliente.id) || false}
                    onClick={() => handleClienteToggle(cliente.id)}
                  >
                    {cliente.nome_fantasia || cliente.nome_social}
                  </ClienteButton>
                ))}
              </ClientesGrid>
            </FormGroup>
          </FormSection>

          <Actions>
            <Button type="button" onClick={handleVoltar}>
              Cancelar
            </Button>
            {isEditing && (
              <>
                <Button type="button" onClick={handleAbrirModalResetSenha} style={{ background: '#f59e0b', color: 'white', border: 'none' }}>
                  <ButtonIcon>
                    <FiLock />
                  </ButtonIcon>
                  Limpar Senha
                </Button>
                <Button type="button" onClick={handleAbrirModalExclusao} $danger>
                  <ButtonIcon>
                    <FiTrash2 />
                  </ButtonIcon>
                  Excluir
                </Button>
              </>
            )}
            <Button $primary type="submit" disabled={loading}>
              <ButtonIcon>
                {loading ? <FiLoader /> : <FiSave />}
              </ButtonIcon>
              {loading ? 'Salvando...' : 'Salvar'}
            </Button>
          </Actions>
        </Form>
      </Container>

      {/* Modal de confirmação de exclusão */}
      {showDeleteModal && (
        <ModalOverlay>
          <ModalContent>
            <ModalTitle>Excluir Usuário</ModalTitle>
            <ModalMessage>
              Tem certeza que deseja excluir este usuário? Esta ação não pode ser desfeita e irá excluir:
              <br /><br />
              • O usuário <strong>{formData.nome}</strong>
              <br />
              • Todas as associações com clientes
              <br />
              • Todos os dados relacionados
            </ModalMessage>
            <ModalActions>
              <ModalButton onClick={handleCancelarExclusao}>
                Cancelar
              </ModalButton>
              <ModalButton $danger onClick={handleExcluirUsuario} disabled={deleting}>
                <FiTrash2 />
                {deleting ? 'Excluindo...' : 'Excluir'}
              </ModalButton>
            </ModalActions>
          </ModalContent>
        </ModalOverlay>
      )}

      {/* Modal de confirmação de limpeza de senha */}
      {showResetPasswordModal && (
        <ModalOverlay>
          <ModalContent>
            <ModalTitle style={{ color: '#f59e0b' }}>Limpar Senha do Usuário</ModalTitle>
            <ModalMessage>
              Tem certeza que deseja limpar a senha do usuário <strong>{formData.nome}</strong>?
              <br /><br />
              Quando limpar a senha:
              <br />
              • O usuário não conseguirá fazer login com a senha atual
              <br />
              • No próximo login, será solicitado que o usuário defina uma nova senha
              <br />
              • Esta ação pode ser útil para casos de esquecimento de senha
            </ModalMessage>
            <ModalActions>
              <ModalButton onClick={handleCancelarResetSenha}>
                Cancelar
              </ModalButton>
              <ModalButton style={{ background: '#f59e0b', color: 'white' }} onClick={handleLimparSenha} disabled={resettingPassword}>
                <FiLock />
                {resettingPassword ? 'Limpando...' : 'Limpar Senha'}
              </ModalButton>
            </ModalActions>
          </ModalContent>
        </ModalOverlay>
      )}
    </Layout>
  );
};

export default UsuarioForm;
